import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as Api from '../../../../extViewmodels';
import { Noop } from '../../../../extViewmodels/Utils';
import { ILocationState } from '../../../../models';
import { CampaignType, MastheadPortalId } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages, useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import {
	getSocialMediaPlatformLogoUrl,
	getSubverticalOptions,
	mapSocialMediaTypesToPostTargets,
} from '../../../../models/UiUtils';
import { CreateGhostCalendarSuggestionViewModel, GhostCalendarViewModel } from '../../../../viewmodels/AdminViewModels';
import { ContentCalendarSuggestionAll } from '../../../../viewmodels/AppViewModels';
import { CampaignCalendar } from '../../../../web/components/campaigns/CampaignCalendar';
import { CampaignCalendarSuggestionsProvider } from '../../../../web/components/campaigns/CampaignCalendar/context';
import { getSuggestionCampaignType } from '../../../../web/components/campaigns/CampaignCalendar/models';
import { CampaignCalendarCard } from '../../../../web/components/campaigns/CampaignCalendarEventCard';
import {
	HeaderDate,
	SuggestionFooter,
} from '../../../../web/components/campaigns/CampaignCalendarEventCard/presentation';
import { LocalNotificationObserver } from '../../../../web/components/LocalNotificationObserver';
import { NavigationBreadcrumbsBar } from '../../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../../web/components/Portal';
import { ContentCalendarTemplateBrowser } from '../../../../web/containers/ContentCalendar/ContentCalendarTemplateBrowser';
import { CalendarType } from '../../../../web/containers/ContentCalendar/models';
import { bs } from '../../../../web/styles/styles';
import { GhostCalendarContext, IGhostCalendarContext } from './context';
import {
	GhostCalendarCampaignTypeOptions,
	GhostCalendarIndustryOptions,
	ISelectedGhostCalendarFilterOptions,
} from './models';
import { GhostCalendarFilters } from './presentation';
import { styleSheet } from './styles';

interface ExtendedDropResult extends DropResult {
	source: DropResult['source'] & { data?: any };
}

interface IProps extends RouteComponentProps<any> {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const GhostCalendarBase: React.FC<IProps> = props => {
	const { className, styles = [] } = props;
	const userSession = useAdminUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();

	const numberOfMonthsToShow = 3;

	const ghostCalendarRef = React.useRef(new GhostCalendarViewModel(userSession));
	const templateBroswerCampaignTypesRef = React.useRef([CampaignType.Email, CampaignType.Social, CampaignType.Blog]);

	const [startDate, setStartDate] = React.useState(moment().startOf('month').toDate());
	const [calendarType, setCalendarType] = React.useState<CalendarType>(CalendarType.Quarterly);
	const [campaignTemplateType, setCampaignTemplateType] = React.useState<CampaignType>(CampaignType.Email);

	const onSuggestionClicked = (suggestion: Api.IContentCalendarSuggestion) => () => {
		const viewModel = CreateGhostCalendarSuggestionViewModel.instanceForEdit(userSession, suggestion);
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel };
		fullscreenModal?.history.push({
			pathname: '/ghost-calendar/suggestion/edit/summary',
			state: locationState,
		});

		return Promise.resolve<void>(null);
	};

	// #region context
	const contextRef = React.useRef<[IGhostCalendarContext, (ctx: IGhostCalendarContext) => void]>(null);
	const [selectedFilterOptions, setSelectedFilterOptions] = React.useState<ISelectedGhostCalendarFilterOptions>({
		ages: [],
		campaignType: GhostCalendarCampaignTypeOptions,
		industries: GhostCalendarIndustryOptions,
		subverticals: [],
	});
	const onSetSelectedFilterOptions = React.useCallback((options: ISelectedGhostCalendarFilterOptions) => {
		// TODO: change url? update vm?
		setSelectedFilterOptions(options);
		if (contextRef.current) {
			const [ctx, setCtx] = contextRef.current;
			const nextCtx = {
				...ctx,
				selectedFilterOptions: options,
			};
			setCtx(nextCtx);
		}
		return Promise.resolve();
	}, []);

	const onLaunchTemplate = React.useCallback(
		(template: Api.ITemplate, startingDate?: string) => {
			const options = selectedFilterOptions;
			const accountAges = !options?.ages?.length
				? [Api.ContentCalendarSuggestionAccountAge.Existing, Api.ContentCalendarSuggestionAccountAge.New]
				: options.ages?.map(x => x.dataContext);
			const industries = options.industries?.map(x => x.dataContext);
			const targets: Api.IPostTarget[] =
				template?.templateType === Api.TemplateType.SocialMediaPost
					? mapSocialMediaTypesToPostTargets(Api.SupportedSocialMediaTypes)
					: [];

			const subverticals =
				options.industries?.length > 1 ? [ContentCalendarSuggestionAll] : options.subverticals?.map(x => x.dataContext);
			const viewModel = CreateGhostCalendarSuggestionViewModel.instanceForCreate(
				userSession,
				template,
				industries,
				subverticals,
				accountAges,
				moment(startingDate),
				targets
			);
			const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> = { viewModel };
			fullscreenModal?.history.push({
				pathname: '/ghost-calendar/suggestion/edit/subverticals',
				state: locationState,
			});
		},
		[selectedFilterOptions, userSession, fullscreenModal]
	);

	const onDragEnd = React.useCallback(
		async (result: ExtendedDropResult) => {
			try {
				if (result.destination?.droppableId && result.destination?.droppableId !== result.source?.droppableId) {
					const templateId = result.draggableId;
					const templates = new Api.TemplatesViewModel(userSession);
					const template = await templates.getById(templateId);
					onLaunchTemplate(template, result.destination.droppableId);
				}
			} catch (e) {
				errorMessages.pushApiError(e);
			}
		},
		[userSession, onLaunchTemplate, errorMessages]
	);

	const onViewTemplate = (template: Api.ITemplate) => {
		onLaunchTemplate(template);
	};

	// Monitor pragmatic-drag-and-drop events and convert them to DropResult format
	React.useEffect(() => {
		const cleanup = monitorForElements({
			onDrop({ source, location: pragmaticLocation }) {
				// Only handle drops that are from template cards to calendar
				if (source.data.type === 'campaignTemplateCard' && pragmaticLocation.current.dropTargets.length > 0) {
					const destination = pragmaticLocation.current.dropTargets[0];

					// Construct a DropResult-like object that matches react-beautiful-dnd format
					const result: ExtendedDropResult = {
						draggableId: source.data.id as string,
						type: source.data.type as string,
						source: {
							droppableId: 'campaignTemplatesList', // This is the template list
							index: source.data.index as number,
							// Embed the template card data in the source for easy access
							data: source.data,
						},
						destination: {
							droppableId: destination.data.id as string,
							index: 0, // Calendar drops don't have a specific index
						},
						reason: 'DROP',
					};

					// Call the same onDragEnd handler
					onDragEnd(result);
				}
			},
		});

		return cleanup;
	}, [onDragEnd]);

	const [context, setContext] = React.useState<IGhostCalendarContext>({
		ghostCalendar: ghostCalendarRef.current,
		selectedFilterOptions,
		setSelectedFilterOptions: onSetSelectedFilterOptions,
	});

	contextRef.current = [context, setContext];
	// #endregion

	const loadSuggestions = React.useCallback(async () => {
		try {
			const options = selectedFilterOptions;
			const accountAges = !options?.ages?.length
				? [Api.ContentCalendarSuggestionAccountAge.Existing, Api.ContentCalendarSuggestionAccountAge.New]
				: options.ages?.map(x => x.dataContext);
			const industries = options.industries?.map(x => x.dataContext);
			let subverticals: string[] = [];

			if (options.industries?.length > 1) {
				subverticals = [ContentCalendarSuggestionAll];
			} else if (options.industries?.length === 1) {
				const industry = options.industries[0].dataContext;

				subverticals =
					getSubverticalOptions(industry).length === options.subverticals?.length
						? [ContentCalendarSuggestionAll]
						: options.subverticals?.map(x => x.dataContext);
			}
			const request: Api.IContentCalendarSuggestionRequest = {
				accountAges,

				industries,
				subverticals,
			};
			const endDate = new Date(startDate);
			endDate.setMonth(endDate.getMonth() + numberOfMonthsToShow);
			await ghostCalendarRef.current.loadAllSuggestions(
				startDate,
				endDate,
				request,
				options.campaignType[0]?.dataContext
			);
		} catch (err) {
			errorMessages.pushApiError(err);
		}
	}, [selectedFilterOptions, errorMessages, startDate, numberOfMonthsToShow]);

	React.useEffect(() => {
		loadSuggestions();
	}, [selectedFilterOptions, loadSuggestions, startDate]);

	const onNotificationReceived = () => loadSuggestions();

	const onMonthsChange = (value: moment.Moment) => {
		setStartDate(value.toDate());
	};

	const onRenderSuggestedEvent = (suggestion: Api.IContentCalendarSuggestion) => {
		const type = getSuggestionCampaignType(suggestion);
		return (
			<CampaignCalendarCard
				key={`suggested-${suggestion.id}`}
				type={type}
				title={suggestion.templateReference.name}
				onCardClick={onSuggestionClicked(suggestion)}
				calendarType={calendarType}
				eventDate={<HeaderDate date={suggestion.schedule.startDate} calendarType={calendarType} />}
				footer={<SuggestionFooter value={false} onClick={Noop} />}
				isSuggestion
			>
				{type === CampaignType.Social && calendarType !== CalendarType.Month
					? suggestion.targets?.map(targetType => {
							return (
								<img
									key={`${targetType}Type-${suggestion.id}}`}
									src={getSocialMediaPlatformLogoUrl(targetType)}
									alt={targetType}
									className={css(styleSheet.socialMediaIcon)}
								/>
							);
						})
					: null}
			</CampaignCalendarCard>
		);
	};

	const getNumberOfMonthsToShow = (): number => {
		switch (calendarType) {
			case CalendarType.Month:
				return 1;
			case CalendarType.Weekly:
				return 0;
			case CalendarType.Quarterly:
			default:
				return 3;
		}
	};

	return (
		<div className={`${css(styleSheet.container, ...styles)} admin-ghost-calendar ${className || ''}`}>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar currentLocationName='Ghost Calendar' />
			</Portal>
			<div className={css(bs.flex, bs.hFull, bs.gap8)}>
				<div className={css(styleSheet.calendarContainer, bs.grid, bs.flex1, bs.pb2)}>
					<GhostCalendarContext.Provider value={context}>
						<GhostCalendarFilters />
						<CampaignCalendarSuggestionsProvider supportsBulkScheduleSuggestions={false}>
							<CampaignCalendar
								calendarType={calendarType}
								className={css(styleSheet.calendar)}
								droppableType='campaignTemplateCard'
								hideCampaigns
								initialStartDate={startDate}
								key={getNumberOfMonthsToShow()}
								numberOfMonthsToShow={getNumberOfMonthsToShow()}
								onCalendarTypeChanged={setCalendarType}
								onMonthsChange={onMonthsChange}
								onRenderSuggestedEvent={onRenderSuggestedEvent}
								showGhost
								suggestions={ghostCalendarRef.current.suggestions}
								selectedCampaignType={campaignTemplateType}
							/>
						</CampaignCalendarSuggestionsProvider>
					</GhostCalendarContext.Provider>
				</div>
				<ContentCalendarTemplateBrowser
					dragDropProps={{
						droppableId: 'campaignTemplatesList',
						type: 'campaignTemplateCard',
						draggingStyles: [
							calendarType === CalendarType.Month
								? styleSheet.campaignTemplateCardDraggingOverDay
								: styleSheet.campaignTemplateCardDragging,
						],
					}}
					onTemplateTypeSelected={setCampaignTemplateType}
					onCampaignTemplateClicked={onViewTemplate}
					hideCreateCampaignsButton
					selectedCampaignType={campaignTemplateType}
					campaignTypes={templateBroswerCampaignTypesRef.current}
				/>
			</div>
			<LocalNotificationObserver onNotificationReceived={onNotificationReceived} topic={Topics.GHOST_CAL_UPDATE} />
		</div>
	);
};

export const GhostCalendar = withRouter(observer(GhostCalendarBase));
